// deployment_class: customer_env_allowed
/**
 * OrgContextSelector — dropdown for switching the active organization
 * context. Plan 14 §Task 6 + Spec 12 §10.2.
 *
 * Renders ONLY when the user has multiple organizations. On switch:
 *   1. Updates the active org in the auth store (Zustand)
 *   2. Per OQ-Sh2 (locked: full route reload), navigates to the current
 *      route with a fresh window.location.assign — guarantees the data
 *      scope refresh is deterministic and avoids partial cache states.
 *
 * In tests we use the `forTestNavigate` prop override so we can assert the
 * intended target without actually triggering a reload.
 */
import { useState } from 'react';
import { useAuth } from '../auth/useAuth';

export interface OrgContextSelectorProps {
  /**
   * Test-only override. Production callers should not pass this — the
   * default is window.location.assign, which forces a full reload.
   */
  forTestNavigate?: (path: string) => void;
}

export function OrgContextSelector({
  forTestNavigate,
}: OrgContextSelectorProps = {}): JSX.Element | null {
  const { organizations, activeOrgId, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);

  if (organizations.length <= 1) return null;

  const active = organizations.find((o) => o.id === activeOrgId) ?? organizations[0];
  if (!active) return null;

  function handleSwitch(orgId: string): void {
    switchOrg(orgId);
    setOpen(false);
    // Full route reload per OQ-Sh2 (locked).
    const target = window.location.pathname + window.location.search;
    if (forTestNavigate) {
      forTestNavigate(target);
    } else {
      window.location.assign(target);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="px-3 py-1 border border-rule bg-paper-soft text-body-s hover:bg-paper-amber"
      >
        Org: <span className="font-medium">{active.name}</span>
        <span className="ml-1" aria-hidden="true">
          ▼
        </span>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Active organization"
          className="absolute right-0 mt-1 min-w-[240px] bg-paper border border-rule shadow-md z-10"
        >
          {organizations.map((org) => (
            <li key={org.id} role="option" aria-selected={org.id === active.id}>
              <button
                type="button"
                onClick={() => handleSwitch(org.id)}
                className={
                  'block w-full text-left px-3 py-2 text-body-s hover:bg-paper-soft ' +
                  (org.id === active.id ? 'bg-paper-blue text-trust' : '')
                }
              >
                {org.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
